import React, { useState, useEffect } from 'react';
import { sanitizeHtml } from '../../utils/sanitize';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from '../../config';
import Modal from 'react-modal';
import { Document, Page, pdfjs } from 'react-pdf';

// Try a different CDN for the PDF.js worker
// Using version 3.11.174 which is more stable and widely available
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`;

// If the above CDN URL doesn't work, we can try the following alternatives:
// Option 1: Use unpkg CDN
// pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@4.8.69/build/pdf.worker.min.js`;
// Option 2: Use jsdelivr CDN
// pdfjs.GlobalWorkerOptions.workerSrc = `//cdn.jsdelivr.net/npm/pdfjs-dist@4.8.69/build/pdf.worker.min.js`;

const customModalStyles = {
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0
    },
    content: {
        position: 'relative',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        maxWidth: '100%',
        maxHeight: '100%',
        padding: 0,
        border: 'none',
        borderRadius: 0,
        backgroundColor: 'transparent',
        overflow: 'hidden'
    }
};

Modal.setAppElement('#root');

const EmailPreview = ({ email, onReply, onBack, onDelete }) => {
    const navigate = useNavigate();
    const [downloadingAttachment, setDownloadingAttachment] = useState(null);
    const [previewAttachment, setPreviewAttachment] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);

    if (!email || !email.email) {
        return (
            <div className="h-[50%] flex flex-col bg-[#FCFCFC] rounded-lg shadow-sm overflow-hidden flex-shrink flex-grow">
                <div className="flex-shrink-0 p-4 bg-gray-50 border-b">
                    <h2 className="text-lg font-bold text-gray-800">Email Preview</h2>
                </div>
                <div className="flex-1 p-6 flex items-center justify-center">
                    <p className="text-gray-500 text-sm">Select an email to preview</p>
                </div>
            </div>
        );
    }
    const handleReplyClick = () => {
        navigate('/reply')
    }

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        try {
            const date = new Date(timestamp);
            if (isNaN(date.getTime())) return '';
            return date.toLocaleString();
        } catch (error) {
            return '';
        }
    };

    const getSenderName = () => {
        if (email.email.display_name)
            return email.email.display_name;
        if (email.email.from_name)
            return email.email.from_name;
        if (email.email.from_email) {
            const emailParts = email.email.from_email.split('@');
            return emailParts[0] || 'Unknown';
        }
        return 'Unknown';
    };

    const getEmailContent = () => {
        // Return the content based on availability
        if (email.email.body_html && email.email.body_html.trim().length > 0) {
            return {
                type: 'html',
                content: email.email.body_html
            };
        }

        if (email.email.body_text && email.email.body_text.trim().length > 0) {
            return {
                type: 'text',
                content: email.email.body_text
            };
        }

        if (email.email.body && email.email.body.trim().length > 0) {
            return {
                type: 'text',
                content: email.email.body
            };
        }

        if (email.email.snippet && email.email.snippet.trim().length > 0) {
            return {
                type: 'text',
                content: email.email.snippet
            };
        }

        // If we get here, we couldn't find any content
        return {
            type: 'text',
            content: 'No content available'
        };
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    const handlePreviewAttachment = async (attachment) => {
        try {
            setDownloadingAttachment(attachment.id);
            const token = Cookies.get('access_token');
            if (!token) {
                throw new Error('Not authenticated');
            }

            console.log("Requesting attachment:", attachment);
            
            const response = await axios.get(
                `${API_URL}/api/attachments/${email.email.id}/${attachment.id}`,
                {
                    responseType: 'blob',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            console.log("Attachment response:", response);
            
            // Determine the MIME type from the response or attachment data
            let mimeType = response.headers['content-type'] || attachment.mimeType || 'application/octet-stream';
            
            // For PDF files, ensure the correct MIME type
            if (attachment.filename?.toLowerCase().endsWith('.pdf')) {
                mimeType = 'application/pdf';
            } else if (attachment.filename?.match(/\.(jpe?g|png|gif|bmp|webp)$/i)) {
                mimeType = `image/${attachment.filename.split('.').pop().toLowerCase().replace('jpg', 'jpeg')}`;
            }
            
            // Create a blob with the correct MIME type
            const file = new Blob([response.data], { type: mimeType });
            const fileUrl = URL.createObjectURL(file);
            
            // For debugging
            console.log("Attachment MIME type:", mimeType);
            console.log("Attachment filename:", attachment.filename);
            
            setPreviewAttachment({ 
                url: fileUrl, 
                type: mimeType,
                filename: attachment.filename || 'download'
            });
        } catch (error) {
            console.error('Error previewing attachment:', error);
            
            // Log more detailed error information
            if (error.response) {
                console.error('Error response:', error.response.status, error.response.data);
            }
            
            if (error.response?.status === 401) {
                alert('Please log in again to preview attachments.');
                navigate('/login');
            } else {
                alert(`Failed to preview attachment: ${error.message || 'Unknown error'}. Please try again.`);
            }
        } finally {
            setDownloadingAttachment(null);
        }
    };

    const getAttachmentUrl = (emailId, attachmentId) => {
        return `${API_URL}/api/attachments/${emailId}/${attachmentId}`;
    };

    // Function to handle successful PDF document loading
    function onDocumentLoadSuccess({ numPages }) {
        setNumPages(numPages);
        setPageNumber(1);
    }

    const handlePdfPreviewError = () => {
        console.log("Falling back to direct download due to PDF.js issues");
        if (previewAttachment) {
            const link = document.createElement('a');
            link.href = previewAttachment.url;
            link.download = previewAttachment.filename || 'document.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // Add cleanup for modal when component unmounts
    useEffect(() => {
        return () => {
            // Clean up any open modals when component unmounts
            if (previewAttachment) {
                setPreviewAttachment(null);
            }
        };
    }, []);

    return (
        <div className="h-[95%] flex flex-col bg-[#FCFCFC] rounded-xl shadow-lg overflow-hidden flex-shrink flex-grow mx-[2%] my-[2%] max-w-[90%]">
            <div className="flex-shrink-0 px-4 py-3 bg-[#FCFCFC] border-b flex items-center justify-between">
                <div className="flex items-center">
                    <button
                        onClick={onBack}
                        className="mr-4 text-gray-600 hover:text-gray-800 flex items-center transition duration-150 ease-in-out"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                    <h2 className="font-bold text-gray-800">Preview</h2>
                </div>
            </div>
            <div className="px-6 py-4 border-b border-gray-200 bg-[#FCFCFC] flex-shrink-0">
                <h1 className="font-bold text-gray-900 mb-3 text-sm">{email.email.subject || 'No subject'}</h1>
                <div className="flex items-center text-xs text-gray-700 mb-2">
                    <span className="font-medium">From:</span>
                    <span className="ml-2 font-semibold">{getSenderName()}</span>
                    <span className="ml-2 text-gray-500 text-xs">&lt;{email.email.from_email || 'unknown@email.com'}&gt;</span>
                </div>
                <div className="text-xs text-gray-500">
                    {(() => {
                        const timestamp = email.email.send_time;
                        if (!timestamp) return '';

                        const now = new Date();
                        const sendTime = new Date(timestamp);
                        const diffInSeconds = Math.floor((now - sendTime) / 1000);

                        if (diffInSeconds < 60) {
                            return 'just now';
                        } else if (diffInSeconds < 3600) {
                            const minutes = Math.floor(diffInSeconds / 60);
                            return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
                        } else if (diffInSeconds < 86400) {
                            const hours = Math.floor(diffInSeconds / 3600);
                            return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
                        } else if (diffInSeconds < 604800) {
                            const days = Math.floor(diffInSeconds / 86400);
                            return `${days} ${days === 1 ? 'day' : 'days'} ago`;
                        } else if (diffInSeconds < 2419200) {
                            const weeks = Math.floor(diffInSeconds / 604800);
                            return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
                        } else {
                            return formatDate(timestamp);
                        }
                    })()}
                </div>
            </div>

            {/* Email Body */}
            <div className="flex-1 px-8 py-6 overflow-y-auto overflow-x-auto bg-[#FCFCFC] min-h-0 border-l border-r border-gray-100 h-[calc(100%-4rem)]" style={{ WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}>
                <div className="prose prose-sm max-w-none mx-auto break-words">
                    <div
                        className="email-content text-sm text-gray-800 font-sans leading-relaxed max-w-full mx-auto"
                        dangerouslySetInnerHTML={{
                            __html: (() => {
                                const content = getEmailContent();
                                if (content.type === 'html') {
                                    // Replace cid: attachments with data URLs
                                    let processedContent = content.content;
                                    if (email.email.attachments && email.email.contentIds) {
                                        // First, create a map of contentId to attachment
                                        const attachmentMap = {};
                                        email.email.attachments.forEach(attachment => {
                                            if (attachment.contentId) {
                                                attachmentMap[attachment.contentId] = attachment;
                                            }
                                        });

                                        // Replace all cid: URLs with actual attachment URLs
                                        Object.entries(email.email.contentIds).forEach(([contentId, attachmentId]) => {
                                            const attachment = email.email.attachments.find(a => a.id === attachmentId);
                                            if (attachment) {
                                                const attachmentUrl = getAttachmentUrl(email.email.id, attachment.id);
                                                processedContent = processedContent.replace(
                                                    new RegExp(`cid:${contentId}`, 'g'),
                                                    attachmentUrl
                                                );
                                            }
                                        });
                                    }
                                    return `
                                        <div class="email-wrapper">
                                            <div class="email-container max-w-full mx-auto">
                                                ${sanitizeHtml(processedContent)}
                                            </div>
                                        </div>
                                    `;
                                } else {
                                    return `<div class="text-content whitespace-pre-wrap break-words">${content.content}</div>`;
                                }
                            })()
                        }}
                    />
                    <style jsx>{`
                        .email-content img { @apply max-w-full h-auto inline-block my-4; }
                        .email-content table { @apply w-full border-collapse border border-gray-200 my-4; }
                        .email-content td, .email-content th { @apply p-3 border border-gray-200 break-words; }
                        .email-content blockquote { @apply text-gray-600 my-4 pl-4 border-l-4 border-gray-300 italic; }
                        .email-content pre, .email-content code { @apply font-mono text-sm p-2 bg-[#FCFCFC] rounded; }
                        .email-content a { @apply text-blue-600 no-underline hover:underline hover:text-blue-800; }
                        .email-content p { @apply my-4 leading-relaxed; }
                        .email-content ul, .email-content ol { @apply my-4 pl-8; }
                        .email-content li { @apply my-2 leading-relaxed; }
                        .email-content pre { @apply bg-[#FCFCFC] p-4 rounded-lg overflow-x-auto my-4 text-sm border border-gray-200 leading-relaxed; }
                        .email-content code { @apply bg-[#FCFCFC] px-2 py-1 rounded text-sm font-mono text-gray-900; }
                        .email-content hr { @apply my-6 border-t border-gray-200; }
                        .email-content [style*="background"] { @apply bg-transparent !important; }
                        .email-content [style*="border"] { @apply border-none !important; }
                        .email-content [style*="font-family"] { @apply font-inherit !important; }
                        .email-content div { @apply my-4 leading-relaxed; }
                    `}</style>
                </div>
            </div>

            {/* Attachments Section */}
            {email.email.attachments && email.email.attachments.length > 0 && (
                <div className="px-4 py-2 border-t bg-[#FCFCFC]">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                        {email.email.attachments.length === 1 ? 'One Attachment' : `${email.email.attachments.length} Attachments`}
                    </h3>
                    <div className="flex flex-wrap gap-3">
                        {email.email.attachments.map((attachment) => (
                            <div
                                key={attachment.id}
                                onClick={() => handlePreviewAttachment(attachment)}
                                className="flex items-center p-2 bg-[#FCFCFC] rounded-lg border border-gray-200 hover:border-blue-400 transition-colors cursor-pointer max-w-[200px]"
                            >
                                <svg className="h-4 w-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                                <div className="flex flex-col min-w-0">
                                    <span className="text-xs font-medium text-gray-700 truncate">{attachment.filename}</span>
                                    <span className="text-xs text-gray-500">{formatFileSize(attachment.size)}</span>
                                </div>
                                {downloadingAttachment === attachment.id && (
                                    <div className="ml-2 animate-spin">
                                        <svg className="h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                        </svg>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {previewAttachment && (
                <Modal
                    isOpen={!!previewAttachment}
                    onRequestClose={() => setPreviewAttachment(null)}
                    contentLabel="Attachment Preview"
                    style={customModalStyles}
                    shouldCloseOnOverlayClick={true}
                    ariaHideApp={false}
                >
                    <div className="relative w-full h-full flex flex-col">
                        <button
                            onClick={() => setPreviewAttachment(null)}
                            className="absolute top-4 right-4 p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors z-50"
                        >
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <div className="flex-1 flex items-center justify-center w-full h-full">
                            {previewAttachment.type.includes('pdf') ? (
                                <div className="pdf-container flex flex-col items-center justify-center w-full h-full">
                                    {/* Floating controls bar at the top */}
                                    <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 bg-white/90 backdrop-blur-sm rounded-full shadow-lg px-4 py-2 flex items-center space-x-4">
                                        <div className="flex items-center">
                                            <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                            </svg>
                                            <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                                                {previewAttachment.filename || 'PDF Document'}
                                            </span>
                                        </div>
                                        <a 
                                            href={previewAttachment.url} 
                                            download={previewAttachment.filename}
                                            className="inline-flex items-center px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs rounded-full transition-colors"
                                        >
                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                            </svg>
                                            Download
                                        </a>
                                    </div>
                                    
                                    {/* PDF Viewer - transparent background with just the PDF */}
                                    <div className="w-full h-full flex items-center justify-center">
                                        {/* Loading indicator overlay */}
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm z-10 pointer-events-none">
                                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                                        </div>
                                        
                                        {/* PDF iframe with transparent background */}
                                        <iframe 
                                            src={previewAttachment.url} 
                                            title="PDF Preview" 
                                            className="w-[85%] h-[90%] border-0"
                                            style={{ 
                                                backgroundColor: 'transparent',
                                                boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
                                            }}
                                            onLoad={(e) => {
                                                // Hide the loading indicator when iframe loads
                                                const loadingOverlay = e.target.previousSibling;
                                                if (loadingOverlay) {
                                                    loadingOverlay.style.display = 'none';
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : previewAttachment.type.includes('image') ? (
                                <div className="image-container flex flex-col items-center justify-center w-full h-full">
                                    {/* Floating controls bar at the top */}
                                    <div className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 bg-white/90 backdrop-blur-sm rounded-full shadow-lg px-4 py-2 flex items-center space-x-4">
                                        <div className="flex items-center">
                                            <svg className="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                            </svg>
                                            <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                                                {previewAttachment.filename || 'Image'}
                                            </span>
                                        </div>
                                        <a 
                                            href={previewAttachment.url} 
                                            download={previewAttachment.filename}
                                            className="inline-flex items-center px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs rounded-full transition-colors"
                                        >
                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                            </svg>
                                            Download
                                        </a>
                                    </div>
                                    
                                    {/* Image Viewer - transparent background with just the image */}
                                    <div className="w-full h-full flex items-center justify-center">
                                        {/* Loading indicator overlay */}
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm z-10 pointer-events-none">
                                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                                        </div>
                                        
                                        {/* Image with subtle shadow */}
                                        <img 
                                            src={previewAttachment.url} 
                                            alt="Attachment Preview" 
                                            className="max-w-[90%] max-h-[90%] object-contain"
                                            style={{ 
                                                filter: 'drop-shadow(0 10px 8px rgba(0, 0, 0, 0.04)) drop-shadow(0 4px 3px rgba(0, 0, 0, 0.1))'
                                            }}
                                            onLoad={(e) => {
                                                // Hide the loading indicator when image loads
                                                const loadingOverlay = e.target.previousSibling;
                                                if (loadingOverlay) {
                                                    loadingOverlay.style.display = 'none';
                                                }
                                            }}
                                            onError={(e) => {
                                                console.error("Image load error");
                                                e.target.onerror = null;
                                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0yNCAxMmMwIDYuNjIzLTUuMzc3IDEyLTEyIDEycy0xMi01LjM3Ny0xMi0xMiA1LjM3Ny0xMiAxMi0xMiAxMiA1LjM3NyAxMiAxMnptLTEgMGMwIDYuMDcxLTQuOTI5IDExLTExIDExcy0xMS00LjkyOS0xMS0xMSA0LjkyOS0xMSAxMS0xMSAxMSA0LjkyOSAxMSAxMXptLTExLjUgNC4xNjZjLS42ODkgMC0xLjI1LS41Ni0xLjI1LTEuMjVzLjU2MS0xLjI1IDEuMjUtMS4yNSAxLjI1LjU2IDEuMjUgMS4yNS0uNTYxIDEuMjUtMS4yNSAxLjI1em0uMDU1LTQuNDY2Yy0uNTM4IDAtLjk5LS40MjgtLjk5LS45NXYtNS41Yy0uMDAxLS41MjMuNDQ3LS45NS45OS0uOTVzLjk5LjQyOC45OS45NXY1LjVjMCAuNTIyLS40NDcuOTUtLjk5Ljk1eiIvPjwvc3ZnPg==';
                                                e.target.style.padding = '20px';
                                                e.target.style.opacity = '0.5';
                                                
                                                // Also hide the loading indicator
                                                const loadingOverlay = e.target.previousSibling;
                                                if (loadingOverlay) {
                                                    loadingOverlay.style.display = 'none';
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="p-4 text-center bg-white rounded-lg">
                                    <p className="text-lg mb-4">This file type cannot be previewed.</p>
                                    <a 
                                        href={previewAttachment.url} 
                                        download={previewAttachment.filename}
                                        className="mt-2 inline-block px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                    >
                                        Download File
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Action Buttons */}
            <div className="py-4 bg-gray-50 flex space-x-3 px-4">
                <button
                    onClick={() => onReply(email)}
                    className="bg-[#1F9BFF] text-white px-5 py-2 rounded-lg hover:bg-blue-500 hover:text-white transition duration-150 ease-in-out flex items-center shadow-md text-xs font-medium"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    Reply
                </button>
                <button
                    onClick={() => onDelete(email.email.id)}
                    className="text-white bg-[#1F9BFF] px-5 py-2 rounded-lg hover:bg-blue-500 hover:text-white transition duration-150 ease-in-out flex items-center text-xs font-medium"
                >
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                </button>
            </div>
        </div>
    );
};

export default EmailPreview;